import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import NextContentLoader from "./NextContentLoader";

export default function PromoDialog({ contents, onContentSeen }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [intervalToWait, setIntervalToWait] = useState(0);
    const [dismissed, setDismissed] = useState(false);

    const contentKey = useMemo(() => contents?.map((c) => c.id).join(",") ?? "", [contents]);
    const firstInterval = contents?.[0]?.interval ?? 0;

    const visible = !dismissed && contents?.length > 0;
    const currentSlide = contents?.[currentIndex];
    const isLastSlide = currentIndex >= (contents?.length ?? 0) - 1;

    // Reset only when the slide set changes (new fetch / different ids), not when viewed flags update
    useEffect(() => {
        if (!contentKey) return;
        setDismissed(false);
        setCurrentIndex(0);
        setIntervalToWait(firstInterval);
    }, [contentKey, firstInterval]);

    useEffect(() => {
        setIntervalToWait(currentSlide?.interval ?? 0);
    }, [currentIndex, currentSlide?.interval]);

    const goPrev = useCallback(() => {
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }, []);

    const goNext = useCallback(() => {
        const slide = contents?.[currentIndex];
        if (slide?.id) {
            onContentSeen?.(slide.id);
        }

        if (isLastSlide) {
            setDismissed(true);
            return;
        }

        setCurrentIndex((prev) => prev + 1);
    }, [contents, currentIndex, isLastSlide, onContentSeen]);

    const onRedirectClick = useCallback(() => {
        if (currentSlide?.redirect_url) {
            window.open(currentSlide.redirect_url, "_blank", "noopener,noreferrer");
        }
    }, [currentSlide?.redirect_url]);

    if (!visible || !currentSlide) {
        return null;
    }

    return (
        <Dialog
            modal
            visible={visible}
            style={{ width: "40vw" }}
            breakpoints={{ "960px": "75vw", "641px": "90vw" }}
            closable={false}
            dismissableMask={false}
            draggable={false}
            resizable={false}
            showHeader={false}
            footer={
                <div className="flex justify-content-between align-items-center gap-2">
                    <Button
                        label="Prev"
                        icon="pi pi-arrow-left"
                        onClick={goPrev}
                        disabled={currentIndex === 0}
                        text
                        severity="secondary"
                    />
                    <NextContentLoader
                        key={currentIndex}
                        intervalToWait={intervalToWait}
                        label={isLastSlide ? "Done" : "Next"}
                        onClick={goNext}
                    />
                </div>
            }
            pt={{
                content: classNames("py-3"),
                footer: classNames("py-3"),
            }}
        >
            <div className="flex flex-column align-items-center gap-2">
                <div
                    className={classNames("w-full border-round overflow-hidden shadow-2", {
                        "cursor-pointer": !!currentSlide?.redirect_url,
                    })}
                    onClick={currentSlide?.redirect_url ? onRedirectClick : undefined}
                    role={currentSlide?.redirect_url ? "link" : undefined}
                >
                    <img
                        src={currentSlide.content}
                        alt="Promo"
                        className="w-full block"
                        style={{ maxHeight: "512px", objectFit: "cover" }}
                    />
                </div>
                {currentSlide?.redirect_url && (
                    <Button label="Open link" icon="pi pi-external-link" text size="small" onClick={onRedirectClick} />
                )}
            </div>
        </Dialog>
    );
}
